import { Box, Button, useToast } from '@chakra-ui/react'
import { ModalBuild } from 'components/molecules'
import { FileGenerator } from 'components/molecules/FileGenerator'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { useAuthContext } from 'contexts/auth'
import { motion } from 'framer-motion'
import useToastMessages from 'hooks/Toast'
import jwt_decode from 'jwt-decode'
import { DocumentTypes } from 'pages/Report/types'
import React, { useEffect, useRef, useState } from 'react'
import ServiceFee from 'services/fee'
import { generateFile } from 'utils/blob'
import formatter from 'utils/formatter'
import {
    FeeRegistration,
    FeeRegistrationElements,
    FeeRegistrationQuery
} from '../../types'
import { FilterFeeManagement } from '../FilterFeeManagement/FilterFeeManagement'
import { FilterFee } from '../FilterFeeManagement/interfaces'
import FormFeeRegistration from './FormRegistration'
import {
    FormRegistrationElementsValues,
    FormRegistrationPayload
} from './FormRegistration/interfaces'

const CURRENT_FEE_BACKGROUND_COLOR = '#C6F6D5'
const FUTURE_FEE_BACKGROUND_COLOR = '#BEE3F8'

type RegistrationMode = 'price' | 'tax'

interface RegistrationProps {
    mode: RegistrationMode
}

const PRICE_TABLE_HEADER: TableBuildProps<FeeRegistrationElements>['header'] =
    [
        {
            name: 'ufTaxa',
            text: 'Código (UF)',
            position: 0
        },
        {
            name: 'descricao',
            text: 'Descrição Detran',
            position: 1
        },
        {
            name: 'tipoVeiculo',
            text: 'Tipo Veículo',
            position: 2
        },
        {
            name: 'cnpjAgenteFinanceiro',
            text: 'CNPJ Agente',
            position: 3
        },
        {
            name: 'publica',
            text: 'Preço',
            position: 4
        },
        {
            name: 'valorRegistradora',
            text: 'Valor Registradora',
            position: 5
        },
        {
            name: 'ativa',
            text: 'Status',
            position: 6
        },
        {
            name: 'dataVigenciaInicio',
            text: 'Data Início de Vigência',
            position: 7
        },
        {
            name: 'dataVigenciaFim',
            text: 'Data Fim de Vigência',
            position: 8
        }
    ]

const TAX_TABLE_HEADER: TableBuildProps<FeeRegistrationElements>['header'] = [
    {
        name: 'ufTaxa',
        text: 'Código (UF)',
        position: 0
    },
    {
        name: 'descricao',
        text: 'Descrição Detran',
        position: 1
    },
    {
        name: 'tipoVeiculo',
        text: 'Tipo Veículo',
        position: 2
    },
    {
        name: 'valorTaxaRegistro',
        text: 'Valor Taxa Registro',
        position: 3
    },
    {
        name: 'valorTaxaAditivo',
        text: 'Valor Taxa Registro Aditivo',
        position: 4
    },
    {
        name: 'ativa',
        text: 'Status',
        position: 5
    },
    {
        name: 'dataVigenciaInicio',
        text: 'Data Início de Vigência',
        position: 6
    },
    {
        name: 'dataVigenciaFim',
        text: 'Data Fim de Vigência',
        position: 7
    }
]

const Registration: React.FC<RegistrationProps> = ({ mode }) => {
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(10)
    const [totalPages, setTotalPages] = useState<number>(1)
    const [filter, setFilter] = useState<FeeRegistrationQuery>({})
    const [fees, setFees] = useState<FeeRegistration[]>([])
    const [itemEdit, setItemEdit] = useState<FeeRegistration>()
    const [loading, setLoading] = useState<boolean>(false)
    const [loadingFile, setLoadingFile] = useState<boolean>(false)

    const actionModal = useRef<ModalBuildHandle>(null)

    const toast = useToast()
    const { currentUser } = useAuthContext()
    const { emitToast } = useToastMessages()

    const tableHeader =
        mode === 'price' ? PRICE_TABLE_HEADER : TAX_TABLE_HEADER

    const registrationName = mode === 'price' ? 'Preço' : 'Taxa'

    const buildQuery = (value?: FilterFee): FeeRegistrationQuery => {
        const commonQuery: FeeRegistrationQuery = {
            dataInicio: value?.dataInicio ?? '',
            dataFim: value?.dataFim ?? '',
            status: value?.status ?? '',
            uf: value?.uf ?? '',
            tipoVeiculo: value?.tipoVeiculo ?? ''
        }

        if (mode === 'tax') {
            return commonQuery
        }

        return {
            ...commonQuery,
            cnpjAgenteFinanceiro: formatter.onlyNumber(
                value?.cnpjAgenteFinanceiro ?? ''
            ),
            publica: value?.publica ?? ''
        }
    }

    const getDateNumber = (value: string): number | null => {
        const formattedDate = formatter.data(value)

        if (!formattedDate) {
            return null
        }

        const [day, month, year] = formattedDate.split('/').map(Number)

        if (!day || !month || !year) {
            return null
        }

        return (year * 10000) + (month * 100) + day
    }

    const getCurrentDateNumber = (): number => {
        const today = new Date()

        return (
            (today.getFullYear() * 10000) +
            ((today.getMonth() + 1) * 100) +
            today.getDate()
        )
    }

    const getFeeRowBackgroundColor = (
        startDateValue: string,
        endDateValue: string
    ): string | undefined => {
        const startDate = getDateNumber(startDateValue)
        const endDate = getDateNumber(endDateValue)
        const currentDate = getCurrentDateNumber()

        if (startDate && currentDate < startDate) {
            return FUTURE_FEE_BACKGROUND_COLOR
        }

        if (
            startDate &&
            endDate &&
            currentDate >= startDate &&
            currentDate <= endDate
        ) {
            return CURRENT_FEE_BACKGROUND_COLOR
        }

        return undefined
    }

    const getVehicleTypeDescription = (
        item: FeeRegistration
    ): string => {
        if (item.tipoVeiculo === 'MOTO') {
            return 'Moto'
        }

        if (item.tipoVeiculo === 'DEMAIS_VEICULOS') {
            return 'Demais veículos'
        }

        return 'Todos'
    }

    const buildTable = (
        items: FeeRegistration[]
    ): TableBuildProps<FeeRegistrationElements>['body'] => {
        /*
         * TableBuild utiliza Record<FeeRegistrationElements, ...>.
         * Por isso todos os campos precisam existir no objeto, mesmo que
         * alguns não estejam no cabeçalho da tela atual.
         */
        return items.map(item => ({
            id: String(item.id ?? item.guid ?? ''),
            rowBackgroundColor: getFeeRowBackgroundColor(
                item.dataVigenciaInicio,
                item.dataVigenciaFim
            ),
            ufTaxa: item.ufTaxa,
            descricao: item.descricao,
            tipoVeiculo: getVehicleTypeDescription(item),
            cnpjAgenteFinanceiro: item.cnpjAgenteFinanceiro
                ? formatter.cnpj(item.cnpjAgenteFinanceiro)
                : 'Padrão',
            publica: item.publica
                ? 'Preço Público Detran'
                : 'Livre concorrência',
            valorRegistradora: formatter.moeda(
                item.valorRegistradora ?? 0
            ),
            valorTaxaRegistro: formatter.moeda(
                item.valorTaxaRegistro ?? 0
            ),
            valorTaxaAditivo: formatter.moeda(
                item.valorTaxaAditivo ?? 0
            ),
            ativa: item.ativa ? 'Ativo' : 'Inativo',
            dataVigenciaInicio: formatter.data(
                item.dataVigenciaInicio
            ),
            dataVigenciaFim: formatter.data(item.dataVigenciaFim)
        }))
    }

    const fetchListFee = async (
        query: FeeRegistrationQuery = filter,
        currentPage = page,
        currentLimit = limit
    ) => {
        setLoading(true)

        const parameters = {
            pagina: currentPage,
            limite: currentLimit,
            query
        }

        try {
            const response =
                mode === 'price'
                    ? await ServiceFee.getFeeRegistration(parameters)
                    : await ServiceFee.getTaxRegistration(parameters)

            setFees(response.data)
            setTotalPages(+response.headers['total-pages'])
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description:
                    'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchListFee(filter, page, limit)
    }, [page, limit])

    const handleFilterFee = (value: FilterFee) => {
        const query = buildQuery(value)

        setPage(1)
        setFilter(query)
        fetchListFee(query, 1, limit)
    }

    const handlePage = (value: number) => {
        setPage(value)
    }

    const handleLimit = (value: number) => {
        setPage(1)
        setLimit(value)
    }

    const openModal = () => {
        actionModal.current?.onOpen()
    }

    const handleCloseModal = () => {
        setItemEdit(undefined)
        actionModal.current?.onClose()
    }

    const add = () => {
        setItemEdit(undefined)
        openModal()
    }

    const handleAction = (
        typeAction: string,
        id: number | string
    ) => {
        if (typeAction !== 'EDIT') {
            return
        }

        const item = fees.find(
            fee =>
                String(fee.id) === String(id) ||
                String(fee.guid) === String(id)
        )

        if (!item) {
            return
        }

        setItemEdit(item)
        openModal()
    }

    const toBoolean = (value: unknown): boolean => {
        return (
            value === true ||
            value === 'true' ||
            value === 'Sim' ||
            value === 'Ativo'
        )
    }

    const moneyToNumber = (value: string | number): number => {
        return Number(String(value ?? '0').replace(/\D/g, '')) / 100
    }

    const payload = (
        data: FormRegistrationElementsValues
    ): FormRegistrationPayload => {
        const deserializeIDToken: any =
            currentUser?.idToken && jwt_decode(currentUser.idToken)

        const commonPayload = {
            ufTaxa: data.ufTaxa,
            descricao: data.descricao,
            ativa: toBoolean(data.ativa),
            dataVigenciaInicio: data.dataVigenciaInicio,
            dataVigenciaFim: data.dataVigenciaFim,
            tipoVeiculo: data.tipoVeiculo,
            usuario: deserializeIDToken
                ? deserializeIDToken['cognito:username']
                : ''
        }

        if (mode === 'tax') {
            return {
                ...commonPayload,
                valorTaxaRegistro: moneyToNumber(
                    data.valorTaxaRegistro
                ),
                valorTaxaAditivo: moneyToNumber(
                    data.valorTaxaAditivo
                )
            }
        }

        const cnpjAgenteFinanceiro = formatter.onlyNumber(
            data.cnpjAgenteFinanceiro ?? ''
        )

        return {
            ...commonPayload,
            cnpjAgenteFinanceiro:
                cnpjAgenteFinanceiro || null,
            publica: toBoolean(data.publica),
            valorRegistradora: moneyToNumber(
                data.valorRegistradora
            )
        }
    }

    const handleSaveModal = async (
        form: FormRegistrationElementsValues
    ) => {
        const isEditing = !!itemEdit?.guid

        try {
            const payloadData = payload(form)

            if (isEditing && itemEdit?.guid) {
                if (mode === 'price') {
                    await ServiceFee.editFeeRegistration(
                        payloadData,
                        itemEdit.guid
                    )
                } else {
                    await ServiceFee.editTaxRegistration(
                        payloadData,
                        itemEdit.guid
                    )
                }
            } else if (mode === 'price') {
                await ServiceFee.createFeeRegistration(payloadData)
            } else {
                await ServiceFee.createTaxRegistration(payloadData)
            }

            handleCloseModal()

            emitToast({
                status: 'success',
                title: `${
                    isEditing ? 'Editar' : 'Cadastrar'
                } ${registrationName}`,
                description: `O cadastro de ${registrationName.toLowerCase()} foi ${
                    isEditing ? 'editado' : 'realizado'
                } com sucesso!`
            })

            fetchListFee(filter, page, limit)
        } catch (error: any) {
            const message =
                error?.response?.data?.[0]?.mensagem ??
                error?.response?.data?.mensagem

            emitToast({
                status: 'error',
                title: `Falha ao ${
                    isEditing ? 'editar' : 'cadastrar'
                } ${registrationName.toLowerCase()}`,
                description:
                    message ??
                    'Houve um erro ao realizar a operação.'
            })
        }
    }

    const downloadFile = async (type: DocumentTypes) => {
        try {
            setLoadingFile(true)

            const commonParameters = {
                uf: filter.uf ?? '',
                dataInicio: filter.dataInicio ?? '',
                dataFim: filter.dataFim ?? '',
                status: filter.status ?? '',
                tipoVeiculo: filter.tipoVeiculo ?? '',
                tipo: type
            }

            const response =
                mode === 'price'
                    ? await ServiceFee.getFileRegistration({
                          ...commonParameters,
                          cnpjAgenteFinanceiro:
                              filter.cnpjAgenteFinanceiro ?? '',
                          publica: filter.publica ?? ''
                      })
                    : await ServiceFee.getFileTaxRegistration(
                          commonParameters
                      )

            generateFile(type, response)

            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description:
                    'Ocorreu um erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingFile(false)
        }
    }

    return (
        <>
            <FilterFeeManagement
                type={mode}
                handleFilterFee={handleFilterFee}
            />

            <Box
                display="flex"
                justifyContent="flex-end"
                margin="24px 0"
                gap="8px"
            >
                <Button colorScheme="primary" onClick={add}>
                    Adicionar {registrationName}
                </Button>

                <FileGenerator
                    callAPI={downloadFile}
                    loading={loadingFile}
                />
            </Box>

            <Box
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <TableBuild
                    header={tableHeader}
                    body={buildTable(fees)}
                    action={[{ type: 'EDIT' }]}
                    onAction={handleAction}
                    loading={{
                        status: loading,
                        loader: Array.from(
                            { length: 11 },
                            () => ({
                                height: '20px'
                            })
                        )
                    }}
                    pagination={{
                        page,
                        limit,
                        totalPages
                    }}
                    onChangePage={handlePage}
                    onChangeLimit={handleLimit}
                />

                <ModalBuild
                    ref={actionModal}
                    closeOnOverlayClick={false}
                    title=" "
                    size="6xl"
                    onCancel={handleCloseModal}
                >
                    <FormFeeRegistration
                        mode={mode}
                        itemEdit={itemEdit}
                        onSubmitForm={handleSaveModal}
                        onCancelForm={handleCloseModal}
                    />
                </ModalBuild>
            </Box>
        </>
    )
}

export default Registration